import React, { useState, useRef, useCallback } from "react";
import { Box, Button, InputAdornment, TextField } from "@mui/material";
import { Add, Search } from "@mui/icons-material";
import ClinicTable from "./ClinicTable";
import DoctorClinicDialog from "../DoctorClinicDialog/DoctorClinicDialog";
import FlatCard from "../../../atoms/FlatCard";

export const ClinicTabContent = () => {
  // `searchInput` follows the TextField keystroke by keystroke; `searchValue`
  // is the debounced term that actually drives the table fetch.
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchValue, setSearchValue] = useState<string>("");
  const [searchDto, setSearchDto] = useState<any>({});
  const [refreshTable, setRefreshTable] = useState(false);
  const [clinicId, setClinicId] = useState<string>();
  const [openDialogCreate, setOpenDialogCreate] = React.useState(false);
  const [openDialogEdit, setOpenDialogEdit] = React.useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setSearchValue(value);
      setSearchDto({ searchTerm: value });
    }, 300);
  }, []);

  const openDialogForCreate = () => setOpenDialogCreate(true);

  const openDialogForUpdate = (clinicId: string) => {
    setOpenDialogEdit(true);
    setClinicId(clinicId);
  };

  const handleCloseDialogs = () => {
    setOpenDialogCreate(false);
    setOpenDialogEdit(false);
    setClinicId(undefined);
  };

  const handleDataSaved = () => {
    setRefreshTable((prev) => !prev);
  };

  // Toolbar modelled after the /registry tabs: compact controls inside a
  // FlatCard rendered above the sticky-header table.
  const toolbar = (
    <FlatCard title="Praxen" sx={{ mb: 1 }} sxContent={{ p: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
        }}
      >
        <TextField
          size="small"
          placeholder="Suchen"
          value={searchInput}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 220 }}
        />

        <Button
          variant="contained"
          size="small"
          startIcon={<Add />}
          onClick={openDialogForCreate}
          sx={{ ml: "auto" }}
        >
          Neu
        </Button>
      </Box>
    </FlatCard>
  );

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Dialog for creating a new clinic, triggered by the toolbar button */}
      <DoctorClinicDialog
        openAddEditDialog={openDialogCreate}
        handleCloseAddEditDialog={handleCloseDialogs}
        onDataSaved={handleDataSaved}
      />
      {/* Dialog for editing an existing clinic, triggered by ClinicTable */}
      <DoctorClinicDialog
        openAddEditDialog={openDialogEdit}
        handleCloseAddEditDialog={handleCloseDialogs}
        onDataSaved={handleDataSaved}
        clinicId={clinicId}
      />

      <ClinicTable
        searchDto={searchDto}
        searchValue={searchValue}
        refresh={refreshTable}
        openDialogForEdit={openDialogForUpdate}
        toolbar={toolbar}
      />
    </Box>
  );
};
